/**
 * Payments Dashboard - analytics overview with metrics and breakdowns.
 */

import { useState, useMemo } from "react";
import { getCurrencySymbol } from "../../../lib/format.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import { IconAlertTriangle, IconRefresh } from "../../../components/Icons.tsx";
import {
  StatsCard,
  ProviderBreakdown,
  CurrencyBreakdown,
  PendingPaymentsBanner,
  PeriodSelector,
  getPeriodDateRange,
  type Period,
} from "./components/index.ts";
import { usePaymentAnalytics } from "./hooks";

// =============================================================================
// Status Labels
// =============================================================================

const STATUS_LABELS: Record<string, string> = {
  CREATED: "Создан",
  AWAITING_PAYMENT: "Ожидает оплаты",
  PENDING_VERIFICATION: "На проверке",
  VERIFIED: "Подтверждён",
  TICKET_ISSUED: "Билеты выданы",
  REJECTED: "Отклонён",
  CANCELLED: "Отменён",
  EXPIRED: "Истёк",
};

const STATUS_ICONS: Record<string, string> = {
  VERIFIED: "✓",
  TICKET_ISSUED: "✓",
  PENDING_VERIFICATION: "⏳",
  AWAITING_PAYMENT: "⏳",
  REJECTED: "✗",
  CANCELLED: "✗",
  EXPIRED: "✗",
};

// =============================================================================
// Component
// =============================================================================

interface PaymentsDashboardProps {
  onNavigateToPending?: () => void;
}

export function PaymentsDashboard({
  onNavigateToPending,
}: PaymentsDashboardProps) {
  const [period, setPeriod] = useState<Period>("month");

  // Build filter based on period
  const filter = useMemo(() => {
    const dateRange = getPeriodDateRange(period);
    if (Object.keys(dateRange).length === 0) return undefined;
    return {
      dateFrom: dateRange.dateFrom?.toISOString(),
      dateTo: dateRange.dateTo?.toISOString(),
    };
  }, [period]);

  const {
    analytics,
    isLoading,
    error: analyticsError,
    refetch,
  } = usePaymentAnalytics({ filter });
  const error = analyticsError?.message ?? null;

  const handlePeriodChange = (newPeriod: Period) => {
    setPeriod(newPeriod);
  };

  const handleRefresh = () => {
    refetch();
  };

  if (isLoading && !analytics) {
    return <PageLoader message="Загрузка аналитики..." />;
  }

  if (error) {
    return (
      <div className="card p-6 text-center">
        <IconAlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
        <h2 className="text-lg font-bold text-slate-900 mb-2">
          Ошибка загрузки
        </h2>
        <p className="text-slate-600 mb-4 text-sm">{error}</p>
        <button onClick={handleRefresh} className="btn-primary w-full">
          Попробовать снова
        </button>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  const totalRevenue = parseFloat(analytics.totalRevenue);
  const averageOrderValue = parseFloat(analytics.averageOrderValue);
  const currencySymbol = getCurrencySymbol(analytics.primaryCurrency);

  // Calculate successful orders count
  const successfulOrders = analytics.byStatus
    .filter((s) => s.status === "VERIFIED" || s.status === "TICKET_ISSUED")
    .reduce((sum, s) => sum + s.count, 0);

  return (
    <div className="space-y-4">
      {/* Header with period selector */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <PeriodSelector value={period} onChange={handlePeriodChange} />
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={isLoading}
          className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-100 text-slate-500 active:bg-slate-200 disabled:opacity-50"
          aria-label="Обновить"
        >
          <IconRefresh
            className={`w-5 h-5 ${isLoading ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Main stats cards */}
      <div className="flex gap-2">
        <StatsCard
          label="Выручка"
          value={totalRevenue.toLocaleString()}
          subtitle={currencySymbol}
          variant="success"
        />
        <StatsCard
          label="Заказов"
          value={analytics.totalOrders}
          subtitle={`${successfulOrders} успешных`}
        />
        <StatsCard
          label="Средний"
          value={averageOrderValue.toLocaleString()}
          subtitle={currencySymbol}
        />
      </div>

      {/* Pending payments banner */}
      <PendingPaymentsBanner
        count={analytics.pendingCount}
        onNavigate={onNavigateToPending}
      />

      {/* Provider breakdown */}
      <ProviderBreakdown
        data={analytics.byProvider}
        totalAmount={totalRevenue}
      />

      {/* Currency breakdown */}
      <CurrencyBreakdown data={analytics.byCurrency} />

      {/* Status breakdown */}
      <div className="card p-4">
        <p className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-3">
          По статусам
        </p>
        <div className="space-y-2">
          {analytics.byStatus.map((item) => {
            const icon = STATUS_ICONS[item.status] || "•";
            const label = STATUS_LABELS[item.status] || item.status;
            const percentage =
              analytics.totalOrders > 0
                ? ((item.count / analytics.totalOrders) * 100).toFixed(0)
                : "0";

            const isSuccess =
              item.status === "VERIFIED" || item.status === "TICKET_ISSUED";
            const isError =
              item.status === "REJECTED" ||
              item.status === "CANCELLED" ||
              item.status === "EXPIRED";

            const textColor = isSuccess
              ? "text-green-700"
              : isError
                ? "text-red-600"
                : "text-slate-700";

            return (
              <div
                key={item.status}
                className="flex items-center justify-between py-1"
              >
                <span className={`text-[13px] ${textColor}`}>
                  {icon} {label}
                </span>
                <span className="text-[13px] text-slate-500">
                  {item.count} ({percentage}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
